<template>
  <div>
    <h5 class="strong">Billing address & Tax</h5>
    <h6>This address appears on your monthly invoice and should be legal address of your home or business</h6>

    <!-- LOADING -->
    <div v-if="mode == 'loading'" class="align-center my-30">
      <Loading />
    </div>

    <!-- VIEW -->
    <div v-else-if="mode == 'view'" class="py-20">
      <div class="flex items-center mb-10">
        <h6 class="strong mb-0">Address</h6>
        <div class="flex-1"></div>
        <Dropdown class="danger mr-15" ref="ddRemove">
          <template slot="toggle">
            <span class="color-danger font-body pointer color-hover-lip">
              <i v-if="removing_details" class="la la-spinner spinning"></i>
              <i v-else class="la la-times"></i>
              Remove
            </span>
          </template>
          <template slot="content">
            <div style="width: 250px">
              <h6 class="strong">Remove billing details</h6>
              <p>This removes all the billing details associated with this account.</p>

              <br />
              <div class="flex items-center">
                <button class="btn btn-outline border-color-white bg-transparent color-white color-white-darkmode" @click="$refs.ddRemove.close()">
                  Cancel
                </button>
                <div class="flex-1"></div>
                <button class="btn bg-white bg-white-darkmode color-danger" @click="removeBillingDetails">Remove</button>
              </div>
            </div>
          </template>
        </Dropdown>
        <button class="btn btn-primary font-h6 free-width px-20" @click="edit"><i class="la la-pencil mr-5"></i>Edit</button>
      </div>

      <h6 v-if="current_data.company_name" class="mb-0">{{ current_data.company_name }}</h6>
      <h6 v-if="current_data.address_line_1" class="mb-0">{{ current_data.address_line_1 }}</h6>
      <h6 v-if="current_data.address_line_2" class="mb-0">{{ current_data.address_line_2 }}</h6>
      <h6>{{ current_data.city }}, {{ current_data.country_name }}</h6>

      <h6 v-if="current_data.tax_id" class="strong">Tax ID: {{ current_data.tax_id }} <i class="la la-check-circle color-green font-h5s"></i></h6>
    </div>

    <!-- EDIT -->
    <div v-else-if="mode == 'edit'">
      <div class="my-10">
        <TextField v-model="company_name" placeholder="Company name" class="font-h6 block" maxlength="100" />
      </div>

      <div class="my-10 pt-20">
        <Dropdown :maxHeight="0" class="block" position="top-left" ref="ddCountry" @toggled-dropdown="toggleDDCountry">
          <template slot="toggle">
            <button class="btn btn-outline font-h6 block has-placeholder" :class="{ 'btn-danger': error_country }">
              <span class="placeholder">Country <span class="color-lip">*</span></span>
              <span class="flex-1 align-left ellipsis">{{ country_name != "" ? country_name : "Choose a country.." }}</span>
              <i class="la la-angle-down ml-20"></i>
            </button>
          </template>
          <template slot="content">
            <div style="width: 400px" class="bg-white border-radius timezone-content">
              <TextField v-model="search_country" class="mb-5 font-h6 block no-title" inputPlaceholder="Search country" ref="searchCountry" />
              <perfect-scrollbar style="height: 300px; max-height: calc(50vh - 100px)" class="ps__child--consume pr-10">
                <div
                  class="btn-selectable"
                  v-for="country in filter_countries"
                  :key="country.code"
                  :class="{ 'bg-primary color-white color-white-darkmode': country_code == country.code }"
                  @click="selectCountry(country)">
                  <span>{{ country.name }}</span>
                </div>
              </perfect-scrollbar>
            </div>
          </template>
        </Dropdown>
      </div>

      <div class="my-10">
        <TextField
          v-model="address_line_1"
          placeholder="Address line 1 <span class='color-lip'>*</span>"
          class="font-h6 block"
          maxlength="100"
          @input="error_address = false"
          :class="{ error: error_address }" />
      </div>

      <div class="my-10">
        <TextField v-model="address_line_2" placeholder="Address line 2" class="font-h6 block" maxlength="100" />
      </div>

      <div class="my-10">
        <TextField
          v-model="city"
          placeholder="Town or City <span class='color-lip'>*</span>"
          class="font-h6 block"
          maxlength="50"
          :class="{ error: error_city }" />
      </div>

      <div class="my-10">
        <TextField
          v-model="postal_code"
          placeholder="Postal code <span class='color-lip'>*</span>"
          class="font-h6 block"
          maxlength="10"
          :class="{ error: error_postal_code }" />
      </div>

      <div class="my-10">
        <TextField v-model="phone" placeholder="Phone" class="font-h6 block" maxlength="20" />
      </div>

      <div class="my-10 relative">
        <TextField v-model="tax_id" placeholder="Tax ID" class="font-h6 block" @input="inputTax" maxlength="20" />
        <span style="position: absolute; right: 10px; bottom: 8px">
          <i class="la la-spinner spinning color-primary" v-if="tax_status == 'loading'"></i>
          <i class="la la-check-circle color-green" v-else-if="tax_status == 'valid'"></i>
          <i class="la la-times-circle color-lip" v-else-if="tax_status == 'invalid'"></i>
        </span>
      </div>

      <h6 class="my-20">
        TAX is not applied to non European (EU) countries.<br />EU registered business can enter their tax identification number to remove tax charges from
        future bills.
      </h6>

      <div v-if="!is_onboard" class="align-right">
        <button v-if="current_data && Object.keys(current_data).length" class="btn btn-outline" v-ripple @click="mode = 'view'">Cancel</button>
        <button class="btn btn-primary" v-ripple @click="save">Save <i class="la la-spinner spinning ml-5" v-if="saving"></i></button>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/service";
import { countries } from "@/constants/scheduling";
export default {
  name: "BillingTax",
  components: {},
  props: {
    is_onboard: {
      default: false,
      type: Boolean,
    },
  },
  data: function () {
    return {
      company_name: "",
      country_code: "",
      country_name: "",
      address_line_1: "",
      address_line_2: "",
      city: "",
      postal_code: "",
      phone: "",
      tax_id: "",

      error_country: false,
      error_address: false,
      error_city: false,
      error_postal_code: false,

      search_country: "",
      countries: countries,
      tax_status: "",
      tax_timeout: false,
      tax_key: 0,
      saving: false,

      current_data: {},
      mode: "loading",
      removing_details: false,
    };
  },
  async mounted() {
    this.loadData();
  },
  computed: {
    filter_countries() {
      return countries.filter(
        (c) => c.name.toLowerCase().includes(this.search_country.toLowerCase()) | c.code.toLowerCase().includes(this.search_country.toLowerCase())
      );
    },
  },
  methods: {
    async loadData() {
      if (this.is_onboard) {
        this.mode = "edit";
      } else {
        this.mode = "loading";
        const response = await request("GET", "stripe/getBillingDetails");
        if (response) {
          let data = response.data;
          if (data) {
            this.current_data = data;
            this.mode = "view";
          } else {
            this.mode = "edit";
          }
        } else {
          this.$root.$emit("alert", "Could not get your settings of Billing address & Tax.", "error");
        }
      }
    },
    selectCountry(country) {
      this.country_code = country.code;
      this.country_name = country.name;
      this.$refs.ddCountry.close();
      this.error_country = false;
    },
    toggleDDCountry(value) {
      if (value) {
        this.search_country = "";
        this.$refs.searchCountry.focus();
      }
    },
    inputTax() {
      this.tax_status = "";
      clearTimeout(this.tax_timeout);

      let key = Math.random();
      this.tax_key = key;

      this.tax_timeout = setTimeout(async () => {
        let tax_id = this.tax_id.trim();
        if (tax_id != "") {
          this.tax_status = "loading";
          const response = await request("POST", "stripe/validateEUVAT", {
            tax_id: tax_id,
            key: key,
          });

          if (response && response.data.request_key == this.tax_key) {
            let data = response.data;
            if (data.valid) {
              this.tax_status = "valid";
              this.company_name = data.name;
              this.address_line_1 = data.address;

              this.error_address = false;

              let country = countries.find((c) => c.code == data.countryCode);
              if (country) {
                this.country_code = country.code;
                this.country_name = country.name;
                this.error_country = false;
              } else {
                this.country_code = "";
                this.country_name = "";
              }
            } else {
              this.tax_status = "invalid";
            }
          }
        }
      }, 1000);
    },
    edit() {
      if (this.removing_details) return false;

      this.company_name = this.current_data.company_name;
      this.country_code = this.current_data.country_code;
      this.country_name = this.current_data.country_name;
      this.address_line_1 = this.current_data.address_line_1;
      this.address_line_2 = this.current_data.address_line_2;
      this.city = this.current_data.city;
      this.postal_code = this.current_data.postal_code;
      this.phone = this.current_data.phone;
      this.tax_id = this.current_data.tax_id;

      if (this.tax_id) this.tax_status = "valid";
      this.mode = "edit";
    },
    async save() {
      if (this.saving) return false;

      let company_name = this.company_name.trim();
      let country_code = this.country_code.trim();
      let country_name = this.country_name.trim();
      let address_line_1 = this.address_line_1.trim();
      let address_line_2 = this.address_line_2.trim();
      let city = this.city.trim();
      let postal_code = this.postal_code.trim();
      let phone = this.phone.trim();
      let tax_id = this.tax_status == "valid" ? this.tax_id : "";

      // Check error
      this.error_address = false;
      this.error_city = false;
      this.error_country = false;
      this.error_postal_code = false;
      let error = false;

      if (country_code == "") {
        this.error_country = true;
        error = true;
      }
      if (address_line_1 == "") {
        this.error_address = true;
        error = true;
      }
      if (city == "") {
        this.error_city = true;
        error = true;
      }
      if (postal_code == "") {
        this.error_postal_code = true;
        error = true;
      }

      if (error) return false;

      let data = {
        company_name: company_name,
        country_code: country_code,
        country_name: country_name,
        address_line_1: address_line_1,
        address_line_2: address_line_2,
        city: city,
        postal_code: postal_code,
        phone: phone,
        tax_id: tax_id,
      };

      if (this.is_onboard) {
        return data;
      }

      this.saving = true;
      const response = await request("POST", "stripe/updateBillingDetails", data);

      if (response) {
        this.$root.$emit("alert", "Your settings has been updated.");
        this.current_data = response.data;
        this.mode = "view";
        this.$emit("added");
      } else {
        this.$root.$emit("alert", "Could not update your settings.", "error");
      }

      this.saving = false;
    },
    async removeBillingDetails() {
      this.$refs.ddRemove.close();
      if (this.removing_details) return false;

      this.removing_details = true;
      const response = await request("DELETE", "stripe/removeBillingDetails");
      if (response) {
        this.current_data = {};
        this.company_name = "";

        this.company_name = "";
        this.country_code = "";
        this.country_name = "";
        this.address_line_1 = "";
        this.address_line_2 = "";
        this.city = "";
        this.postal_code = "";
        this.phone = "";
        this.tax_id = "";

        this.tax_status = "";

        this.mode = "edit";
        this.$emit("removed");
      } else {
        this.$root.$emit("alert", "Could not remove billing details.", "error");
      }
      this.removing_details = false;
    },
  },
};
</script>
